import React, { useState, useEffect, useCallback, useRef } from 'react'
import { ViewMode, ToastMessage, AudioState } from '../types'
import * as api from '../lib/api'
import { fetchSavedResearch, unsaveResearchArticle, unsaveIntelligenceReport, getAuthHeader } from '../lib/api'
import PageHeader from '../components/PageHeader'
import { LoadingAnimation } from '../components/ui/LoadingAnimation'
import { analytics, useScreenEngagement } from '../lib/analytics'
import { CockpitRuntime } from '../components/cockpit/CockpitRuntime'
import { compileViews } from '../lib/cockpit/compiler/viewCompiler'
import { DEFAULT_PERSONAS, type CIG } from '../lib/cockpit/schemas'
import { normalizeLegacyMetrics } from '../lib/cockpit/normalizers'
import {
  buildPodcastAudioState,
  getCompactPodcastActionLabel,
  getPodcastActionState,
  resolveIntelligencePodcastPlayback,
} from '../lib/podcastPlayback'
import { hydratePodcastSurfaceState } from '../lib/podcastHydration'
import { API_BASE_URL } from '../lib/apiBaseUrl'
import { filterProductionDiscoveryItems } from '../lib/reportDiscovery'
import { resolveAssetUrl, resolveResearchCardImageUrl } from '../lib/urlFormatting'

// Interface for intelligence report cockpit data
interface IntelligenceReportData {
  cig: CIG
  sib: any
  written: any
  scenario: any
  qa: any
  plan: any
  deepResearch?: { text?: string; citations?: any[]; sources?: any[]; status?: string; provider?: string } | null
}

interface SavedPageProps {
  viewMode: ViewMode
  onAudioStateChange: (state: AudioState) => void
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void
  onNavigateToProfile?: (userEmail: string) => void
  onArticleOpenChange?: (isOpen: boolean) => void
}

interface SavedResearchArticle {
  id: number | string
  title: string
  query: string
  model: string
  status: string
  privacy: string
  results?: any
  summary?: string
  category?: string  // Actual category from database
  created_at: string
  completed_at?: string
  author_name?: string
  author_avatar?: string
  author_email?: string
  saved_at?: string
  research_id?: number
  saved_id?: number  // ID from saved_articles table
  image_url?: string // Added image URL field
  // Intelligence report fields
  is_intelligence_report?: boolean
  intelligence_report_id?: string
  depth?: string
  is_public?: boolean
  podcast_status?: 'missing' | 'queued' | 'processing' | 'ready' | 'failed'
  podcast_audio_ready?: boolean
  podcast_duration_seconds?: number
  podcast_error_message?: string
}

// Category definitions for Netflix-style grouping
const CATEGORIES = [
  { id: 'recent', name: 'Recent', keywords: [] },
  { id: 'finance', name: 'Finance', keywords: ['finance', 'economic', 'market', 'stock', 'investment', 'crypto', 'banking', 'money', 'trading', 'financial', 'economy', 'business', 'commerce'] },
  { id: 'technology', name: 'Technology', keywords: ['technology', 'tech', 'ai', 'artificial', 'software', 'computer', 'digital', 'cyber', 'data', 'algorithm', 'innovation'] },
  { id: 'history', name: 'History', keywords: ['history', 'historical', 'ancient', 'war', 'civilization', 'past', 'heritage', 'archaeological', 'historic'] },
  { id: 'science', name: 'Science', keywords: ['science', 'scientific', 'research', 'study', 'experiment', 'theory', 'physics', 'chemistry', 'biology', 'quantum'] },
  { id: 'medicine', name: 'Medicine', keywords: ['medicine', 'medical', 'health', 'healthcare', 'disease', 'treatment', 'therapy', 'drug', 'clinical', 'patient', 'doctor', 'pharmaceutical'] },
  { id: 'politics', name: 'Politics', keywords: ['politics', 'political', 'government', 'policy', 'election', 'democracy', 'legislation', 'diplomatic', 'governance'] },
  { id: 'culture', name: 'Culture', keywords: ['culture', 'cultural', 'society', 'social', 'community', 'heritage', 'tradition', 'customs', 'lifestyle', 'anthropology'] },
  { id: 'arts', name: 'Arts', keywords: ['art', 'music', 'film', 'movie', 'entertainment', 'creative', 'design', 'media', 'artistic', 'performance'] },
  { id: 'environment', name: 'Environment', keywords: ['environment', 'climate', 'nature', 'ecology', 'sustainability', 'green', 'renewable', 'conservation', 'pollution'] }
]

const getSavedArticleUiKey = (article: SavedResearchArticle): string => {
  if (typeof article.saved_id === 'number') {
    return `saved-${article.saved_id}`
  }
  if (article.is_intelligence_report && article.intelligence_report_id) {
    return `intel-${article.intelligence_report_id}`
  }
  if (typeof article.research_id === 'number' && article.research_id > 0) {
    return `research-${article.research_id}`
  }
  return `article-${String(article.id)}`
}

const removeSavedArticleFromCollection = (
  articles: SavedResearchArticle[],
  target: SavedResearchArticle
): SavedResearchArticle[] => {
  const targetKey = getSavedArticleUiKey(target)
  return articles.filter(article => getSavedArticleUiKey(article) !== targetKey)
}

const resolveSavedArticleResearchId = (article: SavedResearchArticle): number | null => {
  const candidateId = article.research_id ?? article.id
  const numericId = typeof candidateId === 'number' ? candidateId : parseInt(String(candidateId), 10)
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null
}

const matchesSavedArticleChange = (
  article: SavedResearchArticle,
  detail: api.SavedArticlesChangedDetail
): boolean => {
  if (detail.kind === 'intelligence') {
    return article.intelligence_report_id === detail.id
  }

  const researchId = resolveSavedArticleResearchId(article)
  return researchId !== null && String(researchId) === detail.id
}

const removeSavedArticlesMatchingChange = (
  articles: SavedResearchArticle[],
  detail: api.SavedArticlesChangedDetail
): SavedResearchArticle[] => {
  if (detail.action !== 'unsave') {
    return articles
  }

  return articles.filter((article) => !matchesSavedArticleChange(article, detail))
}

export default function SavedPage({ viewMode, onAudioStateChange, onShowToast, onNavigateToProfile, onArticleOpenChange }: SavedPageProps) {
  // Screen engagement tracking
  const engagementTracking = useScreenEngagement({ screenName: 'saved' })
  const trackedSavedArticleIdsRef = useRef<Set<string>>(new Set())
  
  const [savedArticles, setSavedArticles] = useState<SavedResearchArticle[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedArticle, setSelectedArticle] = useState<SavedResearchArticle | null>(null)
  const [isArticleHydrating, setIsArticleHydrating] = useState(false)
  const [viewType, setViewType] = useState<'carousel' | 'list'>('carousel')
  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0
  })
  // State for intelligence reports with cockpit data
  const [intelligenceReportData, setIntelligenceReportData] = useState<IntelligenceReportData | null>(null)
  const [showCockpit, setShowCockpit] = useState(false)
  const [removingArticleKeys, setRemovingArticleKeys] = useState<Set<string>>(new Set())
  
  useEffect(() => {
    onArticleOpenChange?.(showCockpit)
  }, [showCockpit, onArticleOpenChange])

  useEffect(() => {
    const onCloseOverlay = () => {
      if (showCockpit) {
        setShowCockpit(false)
        setIntelligenceReportData(null)
        setSelectedArticle(null)
        onArticleOpenChange?.(false)
      }
    }
    window.addEventListener('insightatlas-close-article', onCloseOverlay as any)
    const onBrowserPop = () => onCloseOverlay()
    window.addEventListener('popstate', onBrowserPop)
    return () => {
      window.removeEventListener('insightatlas-close-article', onCloseOverlay as any)
      window.removeEventListener('popstate', onBrowserPop)
    }
  }, [showCockpit, onArticleOpenChange])
  const [carouselScrollState, setCarouselScrollState] = useState<{ 
    [key: string]: { canScrollLeft: boolean; canScrollRight: boolean } 
  }>({})

  // Refs for carousel scrolling
  const carouselRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const savedArticleLoadRequestIdRef = useRef(0)

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    analytics.trackPageViewed({
      page: 'saved',
      referrer: document.referrer,
      view_mode: viewMode,
    })
  }, [viewMode])

  const loadSavedArticles = useCallback(async (options?: { silent?: boolean; fresh?: boolean }) => {
    const silent = options?.silent ?? false
    const fresh = options?.fresh ?? false
    const requestId = savedArticleLoadRequestIdRef.current + 1
    savedArticleLoadRequestIdRef.current = requestId
    try {
      if (!silent) {
        setIsLoading(true)
      }
      const response = await fetchSavedResearch({ bypassCache: fresh })
      const normalized = filterProductionDiscoveryItems(response || []).map((article: any) => {
        // Construct image_url from has_image flag if no direct image_url provided
        let imageUrl = article.image_url || article.image_data || article.image || null
        if (!imageUrl && article.has_image) {
          // For intelligence reports without a linked research_id, use intelligence report image endpoint
          if (article.is_intelligence_report && article.intelligence_report_id) {
            imageUrl = `/images/intelligence_${article.intelligence_report_id}.png`
          } else if (article.id && article.id !== 0) {
            imageUrl = `/images/research_${article.id}.png`
          }
        }
        return {
          ...article,
          author_avatar: resolveAssetUrl(
            article.author_avatar || article.avatar_url || article.author?.avatar_url || null,
            API_BASE_URL
          ) || null,
          image_url: resolveResearchCardImageUrl(imageUrl, API_BASE_URL),
          category: article.category || 'General',
        }
      })

      if (savedArticleLoadRequestIdRef.current !== requestId) {
        return
      }

      setSavedArticles(normalized)
    } catch (error) {
      if (savedArticleLoadRequestIdRef.current !== requestId) {
        return
      }
      console.error('Failed to load saved articles:', error)
      onShowToast({
        type: 'error',
        title: 'Failed to load saved articles',
        description: 'Please try again later.'
      })
    } finally {
      if (!silent && savedArticleLoadRequestIdRef.current === requestId) {
        setIsLoading(false)
      }
    }
  }, [onShowToast])

  useEffect(() => {
    loadSavedArticles({ fresh: true })
  }, [loadSavedArticles])

  useEffect(() => {
    savedArticles.forEach((article) => {
      const trackingId = article.intelligence_report_id || article.id?.toString() || article.research_id?.toString()
      if (!trackingId || trackedSavedArticleIdsRef.current.has(trackingId)) return

      trackedSavedArticleIdsRef.current.add(trackingId)
      engagementTracking.trackCardViewed()
    })
  }, [engagementTracking, savedArticles])
  
  // Listen for save/unsave events to reload the list
  useEffect(() => {
    const handleSaveChange = (event: Event) => {
      const detail = (event as CustomEvent<api.SavedArticlesChangedDetail | undefined>).detail
      if (detail?.action === 'unsave') {
        setSavedArticles((prev) => removeSavedArticlesMatchingChange(prev, detail))
        return
      }

      console.log('Save change detected, reloading saved articles...')
      // Refresh in the background so the Saved page does not freeze behind a spinner
      loadSavedArticles({ silent: true, fresh: true })
    }
    
    window.addEventListener('saved-articles-changed', handleSaveChange as EventListener)
    return () => window.removeEventListener('saved-articles-changed', handleSaveChange as EventListener)
  }, [loadSavedArticles])

  const handleArticleSelect = async (article: SavedResearchArticle) => {
    // For intelligence reports, we can have id=0 if there's no linked research_id
    // In that case, use intelligence_report_id for navigation
    const hasValidId = article?.id && article.id !== 0
    const hasIntelligenceReportId = article?.intelligence_report_id
    
    if (!hasValidId && !hasIntelligenceReportId) return

    // Track card click for engagement
    engagementTracking.trackCardClicked()
    
    // Use the appropriate ID for tracking and navigation
    const trackingId = hasValidId ? article.id.toString() : (hasIntelligenceReportId || 'unknown')
    analytics.trackResearchViewed({
      research_id: trackingId,
      report_id: hasIntelligenceReportId || undefined,
      source: 'saved',
      author_is_self: false,
      category: article.category,
    })

    // Use intelligence_report_id for navigation if no valid research id
    const navId = hasValidId ? article.id : `intel_${hasIntelligenceReportId}`
    const targetHash = `#saved/article/${navId}`
    if (window.location.hash !== targetHash) {
      window.history.pushState({ tab: 'saved', modal: 'saved-article' }, '', targetHash)
    }

    // Check if this is an intelligence report
    const isIntelligenceReport = article.is_intelligence_report || article.model === 'intelligence'
    
    if (isIntelligenceReport && article.intelligence_report_id) {
      // Fetch intelligence report data for cockpit
      setSelectedArticle(article)
      setIsArticleHydrating(true)
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/intelligence/report/${article.intelligence_report_id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        })
        
        if (response.ok) {
          const reportData = await response.json()
          const hydratedPodcastState = hydratePodcastSurfaceState(article, reportData)
          
          // Check if we have cockpit data (CIG and SIB)
          if (reportData.report_type === 'intelligence' && reportData.cig && reportData.sib) {
            console.log('[SavedPage] Intelligence report has cockpit data, loading cockpit view')
            const deepResearchPayload = reportData.deep_research || reportData.deepResearch || null
            setIntelligenceReportData({
              cig: reportData.cig,
              sib: reportData.sib,
              written: reportData.written,
              scenario: reportData.scenario,
              qa: reportData.qa,
              plan: reportData.plan,
              deepResearch: deepResearchPayload ? {
                text: deepResearchPayload.text || deepResearchPayload.content || '',
                sources: deepResearchPayload.sources || [],
                citations: deepResearchPayload.citations || [],
                status: deepResearchPayload.status,
                provider: deepResearchPayload.provider,
              } : null
            })
            setSavedArticles((prev) => prev.map((candidate) => (
              candidate.id === article.id
                ? { ...candidate, ...hydratedPodcastState }
                : candidate
            )))
            // Update article with fetched data
            setSelectedArticle({
              ...article,
              title: reportData.title || article.title || article.query,
              author_name: reportData.author_name || article.author_name,
              author_email: reportData.author_email || article.author_email,
              author_avatar: resolveAssetUrl(reportData.author_avatar || article.author_avatar, API_BASE_URL) || article.author_avatar,
              image_url: resolveAssetUrl(reportData.image_url, API_BASE_URL) || article.image_url,
              ...hydratedPodcastState,
            })
            setShowCockpit(true)
            setIsArticleHydrating(false)
            return
          }
        }
      } catch (error) {
        console.error('[SavedPage] Error fetching intelligence report:', error)
      }
      setIsArticleHydrating(false)
    }
  }

  const handlePlayAudio = async (article: SavedResearchArticle) => {
    const reportId = article.intelligence_report_id
    const trackingId = String(article.research_id ?? article.id)
    if (!article.is_intelligence_report || !reportId) {
      return
    }

    const applyPodcastState = (
      next: SavedResearchArticle,
      podcastInfo: api.PodcastInfo
    ): SavedResearchArticle => ({
      ...next,
      podcast_status: (podcastInfo.audio_url ? 'ready' : podcastInfo.status) || next.podcast_status || 'missing',
      podcast_audio_ready: Boolean(podcastInfo.audio_url),
      podcast_duration_seconds: podcastInfo.duration_seconds ?? next.podcast_duration_seconds,
      podcast_error_message: podcastInfo.error_message ?? undefined,
    })

    const updatePodcastState = (podcastInfo: api.PodcastInfo) => {
      setSavedArticles((prev) => prev.map((candidate) => (
        candidate.id === article.id ? applyPodcastState(candidate, podcastInfo) : candidate
      )))
      setSelectedArticle((prev) => (
        prev && prev.id === article.id ? applyPodcastState(prev, podcastInfo) : prev
      ))
    }

    if (
      audioState.topic?.intelligence_report_id === reportId &&
      audioState.topic?.audio_url
    ) {
      const nextAudioState = {
        ...audioState,
        isPlaying: !audioState.isPlaying,
      }
      setAudioState(nextAudioState)
      onAudioStateChange(nextAudioState)
      return
    }

    try {
      let pendingToastShown = false
      const podcastInfo = await resolveIntelligencePodcastPlayback(reportId, {
        onPending: (pending) => {
          updatePodcastState(pending)
          if (pendingToastShown) return
          pendingToastShown = true
          onShowToast({
            type: 'info',
            title: 'Preparing Podcast',
            description: 'Podcast generation has started. Playback will begin as soon as audio is ready.',
          })
        },
      })
      updatePodcastState(podcastInfo)

      if (!podcastInfo.audio_url) {
        onShowToast({
          type: podcastInfo.status === 'failed' ? 'error' : 'warning',
          title: podcastInfo.status === 'failed' ? 'Podcast failed' : 'Podcast still processing',
          description:
            podcastInfo.error_message ||
            'The podcast is still being generated. Please try again in a moment.',
        })
        return
      }

      const topic = {
        id: article.id.toString(),
        title: article.title,
        description: article.query,
        summary: article.summary || '',
        category: article.category || 'General',
        trending_percentage: 0,
        engagement_count: 0,
        sources: article.results?.sources || [],
        created_at: article.created_at,
        tags: [],
        model: article.model,
        query: article.query,
        author: {
          name: article.author_name || 'Unknown',
          avatar_url: article.author_avatar,
          email: article.author_email,
        },
        image_url: article.image_url,
        is_intelligence_report: true,
        intelligence_report_id: article.intelligence_report_id,
        podcast_status: article.podcast_status,
        podcast_audio_ready: article.podcast_audio_ready,
      }

      const newAudioState = buildPodcastAudioState(topic, podcastInfo)
      engagementTracking.trackPodcastPlayed()
      analytics.trackPodcastPlayed({
        research_id: trackingId,
        report_id: reportId,
        duration_listened_seconds: 0,
        completion_rate_percent: 0,
        source: 'article',
      })
      onAudioStateChange(newAudioState)
      setAudioState(newAudioState)
    } catch (e) {
      console.error('Failed to create/play podcast:', e)
      onShowToast({
        type: 'error',
        title: 'Podcast failed',
        description: 'Unable to generate or play the podcast. Please try again later.'
      })
    }
  }

  const selectedArticlePodcastAction = selectedArticle
    ? getPodcastActionState(selectedArticle as any, audioState)
    : null

  const handleUnsave = async (article: SavedResearchArticle) => {
    const articleKey = getSavedArticleUiKey(article)
    if (removingArticleKeys.has(articleKey)) {
      return
    }

    setRemovingArticleKeys(prev => {
      const next = new Set(prev)
      next.add(articleKey)
      return next
    })

    try {
      let removed = false

      if (article.is_intelligence_report && article.intelligence_report_id) {
        removed = await unsaveIntelligenceReport(article.intelligence_report_id)
      } else {
        const researchId = resolveSavedArticleResearchId(article)
        if (researchId) {
          removed = await unsaveResearchArticle(researchId)
        }
      }

      if (!removed) {
        throw new Error('Unable to remove saved article')
      }

      setSavedArticles(prev => removeSavedArticleFromCollection(prev, article))
      
      // Track unsave for engagement
      engagementTracking.trackUnsaved()
      analytics.trackArticleUnsaved(String(article.research_id ?? article.id), article.intelligence_report_id || undefined)
      
      onShowToast({
        type: 'success',
        title: 'Article removed',
        description: 'The article has been removed from your saved collection.'
      })
    } catch (error) {
      console.error('Failed to unsave article:', error)
      onShowToast({
        type: 'error',
        title: 'Failed to remove article',
        description: 'Please try again later.'
      })
    } finally {
      setRemovingArticleKeys(prev => {
        const next = new Set(prev)
        next.delete(articleKey)
        return next
      })
    }
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  // Categorize articles
  const categorizeArticles = () => {
    const categorized: { [key: string]: SavedResearchArticle[] } = {}
    
    // Initialize categories
    CATEGORIES.forEach(cat => {
      categorized[cat.id] = []
    })
    
    // Use filtered articles instead of all saved articles
    const articlesToCategorize = filteredArticles
    
    // Categorize by actual database category field
    articlesToCategorize.forEach(article => {
      // Use the actual category from the database
      const categoryLower = (article.category || 'general').toLowerCase()
      
      // Map database categories to display categories
      const categoryMapping: { [key: string]: string } = {
        'finance': 'finance',
        'technology': 'technology',
        'history': 'history',
        'healthcare': 'medicine',
        'health': 'medicine',
        'medicine': 'medicine',
        'science': 'science',
        'politics': 'politics',
        'business': 'finance',
        'environment': 'environment',
        'culture': 'culture',
        'arts': 'arts',
        'entertainment': 'arts',
        'sports': 'culture',  // Map sports to culture
        'biography': 'history',  // Map biography to history
        'general': 'science',  // Map general to science
        'infrastructure': 'technology',  // Map infrastructure to technology
        'energy': 'environment',  // Map energy to environment
        'space': 'science',  // Map space to science
        'military': 'politics',  // Map military to politics
        'law': 'politics',  // Map law to politics
        'real estate': 'finance',  // Map real estate to finance
        'fashion': 'arts',  // Map fashion to arts
        'food': 'culture',  // Map food to culture
        'travel': 'culture',  // Map travel to culture
        'education': 'culture',  // Map education to culture
        'transportation': 'technology',  // Map transportation to technology
        'security': 'technology'  // Map security to technology
      }
      
      // Default to science if no mapping found (more neutral than technology)
      let mappedCategory = categoryMapping[categoryLower] || 'science'
      
      // If the article is categorized as 'general', try to determine from content
      if (categoryLower === 'general' || categoryLower === 'research' || !categoryMapping[categoryLower]) {
        // First try the getCategoryForTitle function
        const betterCategory = api.getCategoryForTitle?.(article.title || '')
        if (betterCategory && betterCategory.toLowerCase() !== 'general') {
          mappedCategory = categoryMapping[betterCategory.toLowerCase()] || betterCategory.toLowerCase()
        } else {
          // Fallback to content analysis
          const content = `${article.title} ${article.summary || ''}`.toLowerCase()
          
          // UAE/Middle East specific
          if (content.includes('uae') || content.includes('dubai') || content.includes('emirates') || 
              content.includes('middle east') || content.includes('arab')) {
            mappedCategory = 'finance' // Business/Finance category
          } else if (content.includes('econom') || content.includes('business') || content.includes('market')) {
            mappedCategory = 'finance'
          } else if (content.includes('tech') || content.includes('software') || content.includes('ai')) {
            mappedCategory = 'technology'
          } else if (content.includes('health') || content.includes('medical') || content.includes('disease')) {
            mappedCategory = 'medicine'
          } else if (content.includes('politic') || content.includes('government') || content.includes('policy')) {
            mappedCategory = 'politics'
          } else if (content.includes('histor') || content.includes('past') || content.includes('ancient')) {
            mappedCategory = 'history'
          } else if (content.includes('science') || content.includes('research') || content.includes('study')) {
            mappedCategory = 'science'
          }
        }
      }
      
      // Ensure the category exists in categorized object (safety check for hot-reload issues)
      if (!categorized[mappedCategory]) {
        categorized[mappedCategory] = []
      }
      categorized[mappedCategory].push(article)
    })
    
    // Add recent category (last 5 saved, regardless of topic)
    if (articlesToCategorize.length > 0) {
      categorized.recent = [...articlesToCategorize]
        .sort((a, b) => 
          new Date(b.saved_at || b.created_at).getTime() - 
          new Date(a.saved_at || a.created_at).getTime()
        )
        .slice(0, 5)
    }
    
    // Remove empty categories and duplicates within each category
    Object.keys(categorized).forEach(key => {
      categorized[key] = Array.from(new Set(categorized[key].map(a => a.id)))
        .map(id => articlesToCategorize.find(a => a.id === id)!)
        .filter(Boolean)
    })
    
    // Remove categories with no articles
    const finalCategorized: { [key: string]: SavedResearchArticle[] } = {}
    Object.entries(categorized).forEach(([key, articles]) => {
      if (articles.length > 0) {
        finalCategorized[key] = articles
      }
    })
    
    return finalCategorized
  }

  // Filter articles based on search
  const filteredArticles = savedArticles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.query.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.author_name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Check if carousel can scroll
  const checkCarouselScroll = (categoryId: string) => {
    const carousel = carouselRefs.current[categoryId]
    if (carousel) {
      const canScrollLeft = carousel.scrollLeft > 0
      const canScrollRight = carousel.scrollLeft < (carousel.scrollWidth - carousel.clientWidth - 1)
      
      setCarouselScrollState(prev => ({
        ...prev,
        [categoryId]: { canScrollLeft, canScrollRight }
      }))
    }
  }

  // Update scroll state for all carousels
  const updateAllCarouselScrollStates = useCallback(() => {
    Object.keys(carouselRefs.current).forEach(categoryId => {
      checkCarouselScroll(categoryId)
    })
  }, [])

  // Check scroll state when articles change or view changes
  useEffect(() => {
    if (viewType === 'carousel') {
      // Small delay to ensure DOM is updated
      setTimeout(updateAllCarouselScrollStates, 100)
      
      // Add resize listener
      const handleResize = () => updateAllCarouselScrollStates()
      window.addEventListener('resize', handleResize)
      
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [filteredArticles, viewType, updateAllCarouselScrollStates])

  // Carousel scroll functions
  const scrollCarousel = (categoryId: string, direction: 'left' | 'right') => {
    const carousel = carouselRefs.current[categoryId]
    if (carousel) {
      const scrollAmount = carousel.offsetWidth * 0.8
      carousel.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
      
      // Update scroll state after scrolling
      setTimeout(() => checkCarouselScroll(categoryId), 300)
    }
  }

  const renderCarouselItem = (article: SavedResearchArticle) => {
    const isRemovingArticle = removingArticleKeys.has(getSavedArticleUiKey(article))
    return (
    <div
      key={getSavedArticleUiKey(article)}
      className={`mobile-card-tap-target flex-shrink-0 cursor-pointer group transition-transform active:scale-[0.99] ${
        viewMode === 'mobile' ? 'w-[280px]' : 'w-[320px]'
      }`}
      onClick={() => handleArticleSelect(article)}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
        {/* Image Section */}
        <div className={`relative ${viewMode === 'mobile' ? 'h-[160px]' : 'h-[180px]'} overflow-hidden`}>
          {article.image_url ? (
            <img
              src={article.image_url}
              alt={article.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
              <svg className="w-16 h-16 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          )}
          


          {/* Private Badge if needed */}
          {article.privacy === 'private' && (
            <div className="absolute top-3 left-3">
              <span className="bg-gray-900/80 text-white px-2.5 py-1 rounded-lg text-xs font-medium">
                Private
              </span>
            </div>
          )}
        </div>
        
        {/* Content Section */}
        <div className={viewMode === 'mobile' ? 'p-4' : 'p-5'}>
          <h3 className={`text-gray-900 dark:text-white font-semibold ${
            viewMode === 'mobile' ? 'text-base' : 'text-lg'
          } mb-2 line-clamp-2 leading-snug`}>
            {article.title}
          </h3>
          
          <div className={`flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 ${
            viewMode === 'mobile' ? 'mb-3' : 'mb-4'
          }`}>
            <button
              onClick={(e) => {
                e.stopPropagation()
                if (article.author_email && onNavigateToProfile) {
                  onNavigateToProfile(article.author_email)
                }
              }}
              className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white transition-colors"
              disabled={!article.author_email}
            >
              {article.author_avatar ? (
                <img src={article.author_avatar} alt="" className="w-5 h-5 rounded-full" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-gray-300 dark:bg-gray-600" />
              )}
              <span className={`truncate ${viewMode === 'mobile' ? 'max-w-[120px]' : 'max-w-[150px]'}`}>
                {article.author_name || 'Unknown'}
              </span>
            </button>
            <span className="text-gray-400 dark:text-gray-500">•</span>
            <span>{new Date(article.saved_at || article.created_at).toLocaleDateString()}</span>
          </div>
          
          {/* Improved action buttons - Always visible with better styling */}
          <div className="flex items-stretch gap-2">
            {(() => {
              const podcastAction = getPodcastActionState(article as any, audioState)
              const compactPodcastLabel = getCompactPodcastActionLabel(podcastAction.label)
              if (!podcastAction.visible) return null
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handlePlayAudio(article)
                  }}
                  disabled={podcastAction.disabled}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-2 ${
                    viewMode === 'mobile'
                      ? 'min-h-[44px] py-2 px-2.5 text-xs font-semibold'
                      : 'py-2.5 px-4 text-sm font-medium'
                  } rounded-xl transition-all hover:scale-105 ${
                    podcastAction.status === 'playing'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : podcastAction.status === 'failed'
                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  } ${podcastAction.disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                  title={podcastAction.label}
                  aria-label={podcastAction.label}
                >
                  {podcastAction.showSpinner ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      <span className="truncate">{viewMode === 'mobile' ? compactPodcastLabel : podcastAction.label}</span>
                    </>
                  ) : podcastAction.status === 'playing' ? (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4H6v16h4V4zm8 0h-4v16h4V4z"/>
                      </svg>
                      <span className="truncate">{viewMode === 'mobile' ? compactPodcastLabel : podcastAction.label}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      <span className="truncate">{viewMode === 'mobile' ? compactPodcastLabel : podcastAction.label}</span>
                    </>
                  )}
                </button>
              )
            })()}
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleArticleSelect(article)
              }}
              className={`flex-1 min-w-0 flex items-center justify-center gap-2 ${
                viewMode === 'mobile'
                  ? 'min-h-[44px] py-2 px-2.5 text-xs font-semibold'
                  : 'py-2.5 px-4 text-sm font-medium'
              } bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-xl transition-all hover:scale-105`}
              title="Read article"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Read
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                // Use id (which is research_id) for API call, saved_id for UI update
                void handleUnsave(article)
              }}
              disabled={isRemovingArticle}
              className={`${viewMode === 'mobile' ? 'p-2' : 'p-2.5'} bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/30 text-red-700 dark:text-red-400 rounded-xl transition-all hover:scale-105 ${isRemovingArticle ? 'opacity-60 cursor-not-allowed hover:scale-100' : ''}`}
              title={isRemovingArticle ? 'Removing...' : 'Remove from saved'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${viewMode === 'mobile' ? '' : 'px-8 py-12'}`}>
      {viewMode === 'mobile' ? (
        <PageHeader title="Saved" />
      ) : null}
      <div className={viewMode === 'mobile' ? 'px-4 py-4' : 'mx-auto max-w-5xl'}>
        {/* Header - Only show on desktop */}
        {viewMode !== 'mobile' && (
          <div className="mb-6">
            <h1 className="text-gray-900 dark:text-white font-bold text-3xl mb-2">
              Saved
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Your personal collection of saved research
            </p>
          </div>
        )}

        {/* Search and View Toggle */}
        <div className="mb-8 flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search saved research..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* View Toggle - Only show on desktop */}
          {viewMode !== 'mobile' && (
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewType('carousel')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewType === 'carousel' 
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4" />
                  </svg>
                  Browse
                </div>
              </button>
              <button
                onClick={() => setViewType('list')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewType === 'list' 
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  List
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingAnimation size={48} />
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4 mx-auto">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </div>
            <h3 className="text-gray-900 dark:text-white text-xl font-semibold mb-2">
              {searchQuery ? 'No matching articles' : 'No saved articles yet'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchQuery ? 'Try a different search term' : 'Start saving interesting research to build your collection'}
            </p>
          </div>
        ) : (
          (viewType === 'carousel' || viewMode === 'mobile') ? (
            // Netflix-style Carousel View
            <div className="space-y-8">
              {Object.entries(categorizeArticles()).map(([categoryId, articles]) => {
                if (articles.length === 0) return null
                const category = CATEGORIES.find(c => c.id === categoryId)!
                  
                  return (
                  <div key={categoryId} className="relative">
                    <div className="flex items-center justify-between mb-4">
                                          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {category.name}
                      <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                        ({articles.length})
                      </span>
                    </h2>
                          </div>
                          
                    <div className="relative group">
                      {/* Left scroll button - Only show if can scroll left */}
                      {carouselScrollState[categoryId]?.canScrollLeft && (
                        <button
                          onClick={() => scrollCarousel(categoryId, 'left')}
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                      )}
                      
                      {/* Carousel container */}
                      <div
                        ref={el => {
                          carouselRefs.current[categoryId] = el
                          // Check scroll state when ref is set
                          if (el) {
                            setTimeout(() => checkCarouselScroll(categoryId), 0)
                          }
                        }}
                        onScroll={() => checkCarouselScroll(categoryId)}
                        className={`flex ${viewMode === 'mobile' ? 'gap-3 mobile-card-scroll-track' : 'gap-4'} overflow-x-auto scrollbar-hide scroll-smooth pb-4`}
                        style={{ 
                          scrollbarWidth: 'none', 
                          msOverflowStyle: 'none',
                          // On mobile, add negative margins to show hint of next card
                          ...(viewMode === 'mobile' ? { 
                            marginRight: '-1rem',
                            paddingRight: '1rem' 
                          } : {})
                        }}
                      >
                        {articles.map(renderCarouselItem)}
                          </div>
                      
                      {/* Right scroll button - Only show if can scroll right */}
                      {carouselScrollState[categoryId]?.canScrollRight && (
                        <button
                          onClick={() => scrollCarousel(categoryId, 'right')}
                          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                      </div>
                    </div>
                  )
                })}
            </div>
          ) : (
            // Minimal List View
            <div className="space-y-4">
              {filteredArticles.map((article) => {
                const isRemovingArticle = removingArticleKeys.has(getSavedArticleUiKey(article))
                return (
                <div
                  key={getSavedArticleUiKey(article)}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-lg transition-all cursor-pointer group overflow-hidden"
                  onClick={() => handleArticleSelect(article)}
                >
                  <div className="flex">
                    {/* Thumbnail */}
                    <div className="relative w-48 h-32 flex-shrink-0">
                      {article.image_url ? (
                        <img
                          src={article.image_url}
                          alt={article.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
                          <svg className="w-12 h-12 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                      

                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between h-full gap-4">
                        {/* Ensure text column can shrink and not overlap actions */}
                        <div className="flex-1 min-w-0 flex flex-col">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-base line-clamp-1 mb-1">
                            {article.title}
                          </h3>
                          {article.summary && (
                            <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-3">
                              {article.summary}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-auto text-sm text-gray-500 dark:text-gray-400">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (article.author_email && onNavigateToProfile) {
                                  onNavigateToProfile(article.author_email)
                                }
                              }}
                              className="flex items-center gap-2 hover:text-gray-900 dark:hover:text-white transition-colors"
                              disabled={!article.author_email}
                            >
                              {article.author_avatar ? (
                                <img
                                  src={article.author_avatar}
                                  alt={article.author_name}
                                  loading="lazy"
                                  className="w-5 h-5 rounded-full"
                                />
                              ) : (
                                <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                              )}
                              <span>{article.author_name || 'Unknown'}</span>
                            </button>
                            <span className="text-gray-400 dark:text-gray-500">•</span>
                            <span>{new Date(article.saved_at || article.created_at).toLocaleDateString()}</span>
                            {article.privacy === 'private' && (
                              <>
                                <span className="text-gray-400 dark:text-gray-500">•</span>
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Private</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions - icon buttons only; never shrink so they remain visible */}
                        <div className="flex items-center gap-2 ml-4 flex-shrink-0 z-10">
                          {(() => {
                            const podcastAction = getPodcastActionState(article as any, audioState)
                            if (!podcastAction.visible) return null
                            return (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePlayAudio(article)
                                }}
                                disabled={podcastAction.disabled}
                                className={`p-2 transition-colors ${
                                  podcastAction.status === 'playing'
                                    ? 'text-blue-600 dark:text-blue-400'
                                    : podcastAction.status === 'failed'
                                      ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400'
                                      : 'text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                                } ${podcastAction.disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                                title={podcastAction.label}
                              >
                                {podcastAction.showSpinner ? (
                                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                  </svg>
                                ) : podcastAction.status === 'playing' ? (
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M10 4H6v16h4V4zm8 0h-4v16h4V4z"/>
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                )}
                              </button>
                            )
                          })()}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleArticleSelect(article)
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            title="Read article"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              void handleUnsave(article)
                            }}
                            disabled={isRemovingArticle}
                            className={`p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors ${isRemovingArticle ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={isRemovingArticle ? 'Removing...' : 'Remove from saved'}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )
        )}
      </div>

      {/* Intelligence Cockpit View for intelligence reports */}
      {showCockpit && selectedArticle && intelligenceReportData && (() => {
        // Compile CIG to views for CockpitRuntime
        try {
          const cig = intelligenceReportData.cig || {}
          const cigMetadata = (cig as any).metadata || {}
          
          // Convert backend ScenarioPackage to frontend scenario_axes format
          const scenarioPackage = intelligenceReportData.scenario
          const scenarioAxes = (() => {
            if (!scenarioPackage?.axis) return (cig as any).scenario_axes ?? []
            const axis = scenarioPackage.axis
            return [{
              axis_id: axis.axis_id || 'default',
              label: axis.label || 'Scenario Analysis',
              description: axis.description,
              states: (axis.states || []).map((state: any) => ({
                state_id: state.state_id,
                label: state.label,
                short_description: state.short_description || state.description,
                is_default: state.is_default,
                probability_band: state.probability_band,
                triggers: (state.triggers || []).map((t: any) => ({
                  trigger_id: t.trigger_id || t.id,
                  description: t.description,
                  observability: t.observability,
                  confidence: t.confidence,
                })),
                assumptions: (state.assumptions || []).map((a: any) => ({
                  assumption_id: a.assumption_id || a.id,
                  text: a.text,
                  rationale: a.rationale,
                })),
                metric_impacts: state.metric_impacts || [],
              })),
              triggers: (scenarioPackage.global_triggers || axis.triggers || []).map((t: any) => ({
                id: t.trigger_id || t.id,
                label: t.label || t.description,
                definition: t.definition || t.description,
                indicator_type: t.indicator_type,
                maps_to_state_transitions: t.maps_to_state_transitions,
                confidence: t.confidence,
              })),
              assumptions: (axis.assumptions || []).map((a: any) => ({
                id: a.assumption_id || a.id,
                text: a.text,
                applies_to_state_id: a.applies_to_state_id,
              })),
            }]
          })()

          const enhancedCig: CIG = {
            ...cig,
            metadata: {
              ...cigMetadata,
              schema_version: cigMetadata.schema_version || '2.2',
              report_id: cigMetadata.report_id || 'intelligence-report',
              title: (() => {
                const sibTitle = intelligenceReportData?.sib?.research_title
                if (sibTitle && sibTitle.length > 10 && sibTitle !== selectedArticle.query) return sibTitle
                if (cigMetadata.title && cigMetadata.title.length > 10 && cigMetadata.title !== selectedArticle.query) return cigMetadata.title
                return cigMetadata.title || selectedArticle.title || selectedArticle.query
              })(),
            },
            sources: (cig as any).sources ?? [],
            claims: (cig as any).claims ?? [],
            entities: (cig as any).entities ?? [],
            metrics: normalizeLegacyMetrics((cig as any).metrics as any),
            relationships: (cig as any).relationships ?? [],
            figures: (cig as any).figures ?? [],
            datasets: (cig as any).datasets ?? [],
            watchlist: (cig as any).watchlist ?? [],
            key_findings: (cig as any).key_findings ?? [],
            executive_summary: (cig as any).executive_summary ?? [],
            bluf: ((cig as any).bluf ?? selectedArticle.title) || selectedArticle.query,
            report_sections: (cig as any).report_sections ?? [],
            scenario_axes: scenarioAxes.length > 0 ? scenarioAxes : (cig as any).scenario_axes,
          }
          
          const selectedPersona = DEFAULT_PERSONAS.find(p => p.persona_id === 'general_exec') || DEFAULT_PERSONAS[0]
          const compiledViews = compileViews(
            enhancedCig,
            selectedPersona,
            intelligenceReportData.sib,
            intelligenceReportData.deepResearch ||
              (intelligenceReportData as any).deep_research ||
              (((intelligenceReportData as any).written_report || (intelligenceReportData as any).deep_research_report || (intelligenceReportData as any).report_text)
                ? { text: (intelligenceReportData as any).written_report || (intelligenceReportData as any).deep_research_report || (intelligenceReportData as any).report_text }
                : undefined)
          )
          
          const heroImageUrl = selectedArticle.image_url ||
            (intelligenceReportData as any)?.image_url ||
            (enhancedCig as any)?.metadata?.hero_image_url
          
          // For intelligence reports, researchId is the linked research_studies.id (can be 0 if not linked)
          // We need a valid positive number for researchId, otherwise undefined
          const numericId = typeof selectedArticle.id === 'number' ? selectedArticle.id : parseInt(String(selectedArticle.id), 10)
          const validResearchId = !isNaN(numericId) && numericId > 0 ? numericId : undefined
          
          const studyMeta = {
            id: selectedArticle.intelligence_report_id ? `ia-${selectedArticle.intelligence_report_id}` : String(selectedArticle.id),
            intelligenceReportId: selectedArticle.intelligence_report_id,
            researchId: validResearchId,
            isSaved: true,
            isPublic: selectedArticle.is_public || selectedArticle.privacy === 'public',
            category: selectedArticle.category,
            model: selectedArticle.model,
            createdAt: selectedArticle.created_at,
            query: selectedArticle.query,
            researcher: selectedArticle.author_name,
            researcherEmail: selectedArticle.author_email,
            researcherAvatarUrl: selectedArticle.author_avatar,
          }
          
          if (!compiledViews) {
            return null
          }
          
          return (
            <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-gray-900">
              <div className={`h-full ${viewMode === 'mobile' ? '' : 'pl-24'} overflow-y-auto`}>
                <CockpitRuntime
                  cig={enhancedCig}
                  views={compiledViews}
                  persona={selectedPersona}
                  onBack={() => {
                    setShowCockpit(false)
                    setIntelligenceReportData(null)
                    setSelectedArticle(null)
                  }}
                  onListen={selectedArticlePodcastAction?.visible && selectedArticle ? () => handlePlayAudio(selectedArticle) : undefined}
                  listenLabel={selectedArticlePodcastAction?.label}
                  listenDisabled={selectedArticlePodcastAction?.disabled}
                  listenLoading={selectedArticlePodcastAction?.showSpinner}
                  viewMode={viewMode === 'mobile' ? 'mobile' : 'desktop'}
                  hasFloatingAudio={Boolean(audioState.topic)}
                  studyMeta={studyMeta}
                  heroImageUrl={heroImageUrl}
                  onNavigateToProfile={(userEmail) => {
                    setShowCockpit(false)
                    setIntelligenceReportData(null)
                    setSelectedArticle(null)
                    onNavigateToProfile?.(userEmail)
                  }}
                />
              </div>
            </div>
          )
        } catch (error) {
          console.error('Error compiling cockpit views:', error)
          return null
        }
      })()}


      {/* CSS for hiding scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
} 